import { lazy, ComponentType, LazyExoticComponent } from 'react';

/*
  Derived the functionality from the source code of this repository
  https://www.npmjs.com/package/react-safe-lazy

  The issue is when we have a fresh deployment, it is very likely
  to encounter a chunk loading error due to the browser cache.

  This retry logic ensures the user sees the latest version.
*/

const STORAGE_KEY = 'sv_safe_lazy_chunk_reloads';

const getReloadCounts = (): Map<string, number> => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? new Map(JSON.parse(stored)) : new Map();
  } catch {
    return new Map();
  }
};

const saveMap = (map: Map<string, number>) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map.entries()]));
  } catch {
    console.log('Failed to save chunk reload counts to sessionStorage');
  }
};

const setReloadCount = (key: string, count: number) => {
  const map = getReloadCounts();
  map.set(key, count);
  saveMap(map);
};

const clearReloadCount = (key: string) => {
  const map = getReloadCounts();
  map.delete(key);
  saveMap(map);
};

type LazyImportFn<T> = () => Promise<{ default: ComponentType<React.PropsWithChildren<T>> }>;

const lazyWithRetry = <T>(
  importFn: LazyImportFn<T>
): LazyExoticComponent<ComponentType<React.PropsWithChildren<T>>> => {
  const key = importFn.toString();

  return lazy(async () => {
    try {
      const module = await importFn();
      clearReloadCount(key);
      return module;
    } catch (error) {
      const reloaded = getReloadCounts().get(key) ?? 0;

      if (reloaded < 1) {
        setReloadCount(key, reloaded + 1);
        globalThis.location.reload();
        return {
          default: (() => undefined) as unknown as ComponentType<React.PropsWithChildren<T>>,
        };
      }

      clearReloadCount(key);
      throw error;
    }
  });
};

export default lazyWithRetry;
